import { FC, Fragment, useEffect } from 'react';
import { provide, useDependencies } from '@servicetitan/react-ioc';
import { observer } from 'mobx-react';
import {
    Frame,
    Form,
    Divider,
    Button,
    Mask,
    Eyebrow,
    Tooltip,
    Stack,
} from '@servicetitan/design-system';

import { Header } from '../common/components/header/header';
import { AccountSettingsStore } from './stores/account-settings.store';
import { AccountSettingsApi } from './api/account-settings.api';
import { DocumentTitle } from '@servicetitan/document-title';
import * as Styles from './account-settings.module.scss';
import { ChangePasswordModal } from './components/change-password-modal';
import { UserPhoneInput } from './components/user-phone-input/user-phone-input';
import { EHStore } from '../common/stores/eh.store';
import { AccountSettingsHeader } from './components/account-settings-header/account-settings-header';
import { FeatureFlagStore } from '@servicetitan/launchdarkly-service';
import { ConfirmMFAEnableDialog } from './components/confirm-mfa-enable-dialog/confirm-mfa-enable-dialog';
import { EnableMfaFormCheckbox } from './components/enable-mfa-form-checkbox';

export const AccountSettingsModule: FC = provide({
    singletons: [AccountSettingsStore, AccountSettingsApi],
})(
    observer(() => {
        const [
            {
                accountSettingsForm,
                loading,
                modalOpen,
                userData,
                confirmMFAEnableDialogOpen,
                getUserDetails,
                openModal,
            },
            { isAdmin },
            { flags },
        ] = useDependencies(AccountSettingsStore, EHStore, FeatureFlagStore);

        const {
            name,
            email,
            officePhone,
            mobilePhone,
            userName,
            mobilePhoneLoading,
            isMfaEnabled,
        } = accountSettingsForm.$;

        useEffect(() => {
            getUserDetails();
        }, [getUserDetails]);

        return (
            <Frame className="main-frame" header={<Header />}>
                <DocumentTitle title="Account Settings" />
                <div className="container-relaxed">
                    <AccountSettingsHeader />
                    <Divider spacing={4} />
                    <div>
                        <Mask active={loading}>
                            <Form className={Styles.accountDetailsForm}>
                                <Form.Group widths="equal">
                                    <Form.Input
                                        id="account_name"
                                        value={name.value}
                                        onChange={name.onChangeHandler}
                                        label="Name"
                                        required
                                        error={name.error}
                                    />
                                    <Form.Input
                                        id="account_email"
                                        value={email.value}
                                        label="Email"
                                        required
                                        disabled
                                        error={email.error}
                                    />
                                </Form.Group>
                                <Stack spacing={3} className="m-b-4">
                                    <Stack.Item fill basis="50%">
                                        <UserPhoneInput
                                            value={officePhone.value}
                                            onChange={officePhone.onChange}
                                            label="Office Phone"
                                            className="qa-office-input"
                                            error={officePhone.error}
                                        />
                                    </Stack.Item>
                                    <Stack.Item fill basis="50%">
                                        {userData?.isMfaEnabled ? (
                                            <Tooltip
                                                el="div"
                                                hoverOnly
                                                direction="right"
                                                text="Please contact your admin to edit your mobile phone number"
                                            >
                                                <UserPhoneInput
                                                    required
                                                    disabled
                                                    value={mobilePhone.value}
                                                    onChange={mobilePhone.onChange}
                                                    label="Mobile Phone"
                                                    className="qa-mobile-input"
                                                    error={mobilePhone.error}
                                                    loading={mobilePhoneLoading?.value}
                                                />
                                            </Tooltip>
                                        ) : (
                                            <UserPhoneInput
                                                value={mobilePhone.value}
                                                onChange={mobilePhone.onChange}
                                                label="Mobile Phone"
                                                className="qa-mobile-input"
                                                error={mobilePhone.error}
                                                loading={mobilePhoneLoading?.value}
                                            />
                                        )}
                                    </Stack.Item>
                                </Stack>

                                <Form.Group widths="equal" className="align-items-end">
                                    <Form.Input
                                        id="account_username"
                                        className="flex-none-i w-50"
                                        value={userName.value}
                                        onChange={userName.onChangeHandler}
                                        label="Username"
                                        required
                                        error={userName.error}
                                    />
                                    {!isAdmin && (
                                        <Button
                                            id="account_change_password_btn"
                                            onClick={openModal}
                                            className="m-l-2"
                                            iconName="lock_outline"
                                            outline
                                        >
                                            Change Password
                                        </Button>
                                    )}
                                </Form.Group>

                                {flags.multiFactorAuthenticationIsEnabled &&
                                    isMfaEnabled &&
                                    !isAdmin && (
                                        <Fragment>
                                            <Divider spacing={2} />
                                            <Eyebrow className="m-b-2">Security</Eyebrow>
                                            <EnableMfaFormCheckbox
                                                isMfaEnabled={userData?.isMfaEnabled}
                                                checked={isMfaEnabled.value}
                                                value={isMfaEnabled.value}
                                                onChange={isMfaEnabled.onChangeHandler}
                                            />
                                        </Fragment>
                                    )}
                            </Form>
                        </Mask>

                        {confirmMFAEnableDialogOpen && <ConfirmMFAEnableDialog />}

                        {modalOpen && <ChangePasswordModal />}
                    </div>
                </div>
            </Frame>
        );
    })
);
